// src/features/executive/sales/widgets/SalesSummaryTiles.tsx
// Hero row for the Sales tab — 4 KPI tiles:
//   Total Revenue (procedure + product)
//   Procedure Revenue
//   Product Revenue
//   Case Count
// Each tile shows vs-previous-period variance underneath the value.
import { Stack, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import EmptyState from 'features/executive/components/EmptyState/EmptyState'
import ErrorState from 'features/executive/components/ErrorState/ErrorState'
import LoadingSkeleton from 'features/executive/components/LoadingSkeleton/LoadingSkeleton'
import KPICard from 'features/executive/components/KPICard/KPICard'
import KPIDetailRow from 'features/executive/components/KPICard/KPIDetailRow'
import {
  addDaysYMD,
  diffDaysInclusive,
  formatCount,
  formatCurrency,
  formatCurrencyCompact,
  formatDateRange,
} from 'utils/executive.utils'
import type { ExecutiveFilterState } from 'api/executive/executive.types'
import { useSalesSummary } from '../hooks/use-sales-summary'

function varianceOf(current: number, previous: number | null): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

// Mirror of backend ../shared/utils.ts::previousRange — same-duration window
// ending the day before `start`. Kept inline so this widget doesn't import
// from the backend service.
function previousRangeOf(start: string, end: string): { from: string; to: string } {
  const days = diffDaysInclusive(start, end)
  const prevEnd = addDaysYMD(start, -1)
  const prevStart = addDaysYMD(prevEnd, -(days - 1))
  return { from: prevStart, to: prevEnd }
}

interface ExecutiveRootState {
  executive: { filters: ExecutiveFilterState }
}

export default function SalesSummaryTiles() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useSalesSummary()
  const filters = useSelector((s: ExecutiveRootState) => s.executive.filters)

  if (loading) return <LoadingSkeleton height={120} />
  if (error) return <ErrorState onRetry={refetch} />
  if (!data) {
    return (
      <EmptyState
        message={t(
          'EXECUTIVE.SALES.SUMMARY.EMPTY_MESSAGE',
          'ไม่พบรายการชำระในช่วงเวลาและสาขาที่เลือก'
        )}
      />
    )
  }
  if (data.totalRevenue === 0) {
    return (
      <EmptyState
        message={t(
          'EXECUTIVE.SALES.SUMMARY.EMPTY_MESSAGE',
          'ไม่พบรายการชำระในช่วงเวลาและสาขาที่เลือก'
        )}
      />
    )
  }

  const totalVar = varianceOf(data.totalRevenue, data.previousTotalRevenue)
  const procVar = varianceOf(data.procedureRevenue, data.previousProcedureRevenue)
  const prodVar = varianceOf(data.productRevenue, data.previousProductRevenue)
  const caseVar = varianceOf(data.caseCount, data.previousCaseCount)
  const medianCaseVar = varianceOf(
    data.medianRevenuePerCase,
    data.previousMedianRevenuePerCase
  )
  const avgPerCase = data.caseCount > 0 ? Math.round(data.procedureRevenue / data.caseCount) : 0

  // Mix share: % of total revenue from procedures vs products.
  const procShare = (data.procedureRevenue / data.totalRevenue) * 100
  const prodShare = (data.productRevenue / data.totalRevenue) * 100

  // Previous-period dates: same-duration window before the current range.
  // Used in the variance tooltip so the user can see *what* we're comparing.
  const prevRange =
    filters.dateRange?.start && filters.dateRange?.end
      ? previousRangeOf(filters.dateRange.start, filters.dateRange.end)
      : null
  const prevRangeLabel = prevRange ? formatDateRange(prevRange.from, prevRange.to) : null

  const prevTooltip = (prevAmount: string) =>
    prevRangeLabel
      ? t('EXECUTIVE.SALES.SUMMARY.VARIANCE_TOOLTIP', 'เทียบกับช่วง {{range}}: {{amount}}', {
          range: prevRangeLabel,
          amount: prevAmount,
        })
      : undefined

  const vsPrevLabel = t('EXECUTIVE.SALES.SUMMARY.VS_PREV', 'vs งวดก่อน')
  const caseUnit = t('EXECUTIVE.SALES.UNIT.CASE', 'เคส')
  const unpaidCases = Math.max(0, data.caseCount - data.paidCaseCount)
  const prevCaseCount = data.previousCaseCount ?? 0
  const prevProcedureRevenue = data.previousProcedureRevenue ?? 0
  const avgPerCasePrev =
    prevCaseCount > 0
      ? prevProcedureRevenue / prevCaseCount
      : 0
  const avgPerCaseVar = varianceOf(avgPerCase, avgPerCasePrev)

  // Title tooltips: formula + exact amount.
  const totalTooltipLines = [
    t(
      'EXECUTIVE.SALES.SUMMARY.TOTAL_TOOLTIP',
      'รวมยอดที่ลูกค้าชำระจริงในช่วงวันที่เลือก = รายได้หัตถการ + รายได้สินค้า (นับตามวันที่ชำระ ไม่นับใบที่ถูกยกเลิก)'
    ),
    '',
    `${t('EXECUTIVE.SALES.SUMMARY.TOTAL', 'ยอดขายรวม')}: ${formatCurrency(data.totalRevenue)}`,
  ].join('\n')

  const procedureTooltipLines = [
    t(
      'EXECUTIVE.SALES.SUMMARY.PROCEDURE_TOOLTIP',
      'รายได้จากบริการรักษา (หัตถการ) ที่ลูกค้าชำระจริงในช่วงวันที่เลือก — ไม่นับการขายสินค้า/วัสดุ และไม่นับใบที่ถูกยกเลิก'
    ),
    '',
    `${t('EXECUTIVE.SALES.SUMMARY.PROCEDURE', 'รายได้หัตถการ')}: ${formatCurrency(data.procedureRevenue)}`,
  ].join('\n')

  const productTooltipLines = [
    t(
      'EXECUTIVE.SALES.SUMMARY.PRODUCT_TOOLTIP',
      'รายได้จากการขายสินค้า/วัสดุ ที่ลูกค้าชำระจริงในช่วงวันที่เลือก — เช่น แปรงสีฟัน, ชุด home-care, retainer แยกขาย (ไม่รวมหัตถการและใบที่ถูกยกเลิก)'
    ),
    '',
    `${t('EXECUTIVE.SALES.SUMMARY.PRODUCT', 'รายได้สินค้า')}: ${formatCurrency(data.productRevenue)}`,
  ].join('\n')

  const casesTooltipLines = [
    t(
      'EXECUTIVE.SALES.SUMMARY.CASES_TOOLTIP',
      'นับจำนวนหัตถการที่ลูกค้ามารับบริการในช่วงวันที่เลือก (ไม่ใช่จำนวนคนไข้) — ลูกค้า 1 คนที่ทำ 3 หัตถการ = 3 เคส และรวมเคสที่ลูกค้ายังไม่ชำระเงิน'
    ),
    '',
    `${t('EXECUTIVE.SALES.SUMMARY.CASES', 'จำนวนเคส')}: ${formatCount(data.caseCount, caseUnit)}`,
  ].join('\n')

  const infoIcon = (tooltip: string) => (
    <Tooltip
      title={tooltip}
      arrow
      placement="top"
      componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
    </Tooltip>
  )

  const tiles = [
    {
      key: 'total',
      title: t('EXECUTIVE.SALES.SUMMARY.TOTAL', 'ยอดขายรวม'),
      value: formatCurrencyCompact(data.totalRevenue),
      headerAction: infoIcon(totalTooltipLines),
      caption: (
        <Stack spacing={0.5}>
          <KPIDetailRow
            label={vsPrevLabel}
            labelTooltip={prevTooltip(formatCurrency(data.previousTotalRevenue))}
            variance={totalVar}
          />
          <Typography variant="caption" color="text.secondary">
            {t(
              'EXECUTIVE.SALES.SUMMARY.TOTAL_BREAKDOWN',
              'หัตถการ {{proc}}% • สินค้า {{prod}}%',
              { proc: procShare.toFixed(0), prod: prodShare.toFixed(0) }
            )}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'procedure',
      title: t('EXECUTIVE.SALES.SUMMARY.PROCEDURE', 'รายได้หัตถการ'),
      value: formatCurrencyCompact(data.procedureRevenue),
      headerAction: infoIcon(procedureTooltipLines),
      caption: (
        <Stack spacing={0.5}>
          <KPIDetailRow
            label={vsPrevLabel}
            labelTooltip={prevTooltip(formatCurrency(data.previousProcedureRevenue))}
            variance={procVar}
          />
          <KPIDetailRow
            label={t('EXECUTIVE.SALES.SUMMARY.AVG_PER_CASE_LABEL', 'เฉลี่ย/เคส')}
            value={formatCurrency(avgPerCase)}
            variance={avgPerCaseVar}
          />
          <KPIDetailRow
            label={t('EXECUTIVE.SALES.SUMMARY.CASES_LABEL', 'เคส')}
            value={data.caseCount.toLocaleString('th-TH')}
            variance={caseVar}
          />
        </Stack>
      ),
    },
    {
      key: 'product',
      title: t('EXECUTIVE.SALES.SUMMARY.PRODUCT', 'รายได้สินค้า'),
      value: formatCurrencyCompact(data.productRevenue),
      headerAction: infoIcon(productTooltipLines),
      caption: (
        <Stack spacing={0.5}>
          {data.productRevenue === 0 ? (
            <Typography variant="caption" color="text.secondary">
              {t('EXECUTIVE.SALES.SUMMARY.PRODUCT_ZERO_STATE', 'ไม่มีการขายสินค้าในช่วงนี้')}
            </Typography>
          ) : (
            <>
              <KPIDetailRow
                label={vsPrevLabel}
                labelTooltip={prevTooltip(formatCurrency(data.previousProductRevenue))}
                variance={prodVar}
              />
              <KPIDetailRow
                label={t('EXECUTIVE.SALES.SUMMARY.PRODUCT_SHARE_LABEL', 'ส่วนแบ่ง')}
                value={`${prodShare.toFixed(0)}%`}
              />
            </>
          )}
        </Stack>
      ),
    },
    {
      key: 'cases',
      title: t('EXECUTIVE.SALES.SUMMARY.CASES', 'จำนวนเคส'),
      value: `${data.caseCount.toLocaleString('th-TH')} ${caseUnit}`,
      headerAction: infoIcon(casesTooltipLines),
      caption: (
        <Stack spacing={0.5}>
          <KPIDetailRow
            label={vsPrevLabel}
            labelTooltip={prevTooltip(formatCount(data.previousCaseCount, caseUnit))}
            variance={caseVar}
          />
          {unpaidCases > 0 && (
            <KPIDetailRow
              label={t('EXECUTIVE.SALES.SUMMARY.PAID_UNPAID_LABEL', 'จ่ายแล้ว · ค้าง')}
              value={`${data.paidCaseCount.toLocaleString('th-TH')} · ${unpaidCases.toLocaleString('th-TH')}`}
            />
          )}
          <KPIDetailRow
            label={t('EXECUTIVE.SALES.SUMMARY.AVG_PER_CASE_LABEL', 'เฉลี่ย/เคส')}
            value={formatCurrency(avgPerCase)}
          />
          <KPIDetailRow
            label={t('EXECUTIVE.SALES.SUMMARY.MEDIAN_PER_CASE_LABEL', 'ค่ากลาง/เคส')}
            labelTooltip={t(
              'EXECUTIVE.SALES.SUMMARY.MEDIAN_PER_CASE_TOOLTIP',
              'ค่ากลางของรายได้ต่อเคส (Median) — สะท้อนเคสทั่วไปดีกว่าค่าเฉลี่ย'
            )}
            value={formatCurrency(data.medianRevenuePerCase)}
            variance={medianCaseVar}
          />
        </Stack>
      ),
    },
  ]

  return (
    <>
      {tiles.map((tile) => (
        <KPICard
          key={tile.key}
          title={tile.title}
          value={tile.value}
          caption={tile.caption}
          headerAction={tile.headerAction}
        />
      ))}
    </>
  )
}
